import { useTicketingModalRootStore } from '../../../../_shared/components/ticketing-modal-root/ticketing-modal-root.store';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useEffect, useMemo } from 'react';
import {
  DeleteLabelTicketValidationSchema,
  TDeleteLabelTicketValidation,
  useDeleteLabelTicketMutation,
} from '../../../api/delete-label-ticket.api';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import { GET_LIST_LABEL_TICKET_QUERY_KEY } from '../../../api/get-list-label-ticket.api';
import {
  ModalCloseButton,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';

const TOAST_MESSAGES_DELETE_LABEL_TICKET = {
  DELETE_LABEL_TICKET_ERROR: 'Error delete label ticket, please try again!',
  DELETE_LABEL_TICKET_SUCCESS: 'Label ticket deleted successfully',
};

export type TDeleteLabelTicketModalType = 'DeleteLabelTicketModal';
export interface IDeleteLabelTicketModalProps {
  labelTicketId: string;
}
const DeleteLabelTicketModal = memo((props: IDeleteLabelTicketModalProps) => {
  // HOOKS
  const showToast = useCustomToast();
  const { actions: modalActions } = useTicketingModalRootStore();
  const form = useForm<TDeleteLabelTicketValidation>({
    resolver: zodResolver(DeleteLabelTicketValidationSchema),
    mode: 'onChange',
  });
  const mutationDeleteLabelTicket = useDeleteLabelTicketMutation({
    mutationConfig: {
      onError: (error) => {
        const errorMessage =
          axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_LABEL_TICKET.DELETE_LABEL_TICKET_ERROR;
        showToast({
          message: errorMessage,
          type: 'error',
        });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_LABEL_TICKET_QUERY_KEY] })]);
        modalActions.closeModal('DeleteLabelTicketModal');
        showToast({
          message: TOAST_MESSAGES_DELETE_LABEL_TICKET.DELETE_LABEL_TICKET_SUCCESS,
          type: 'success',
        });
      },
    },
  });

  // HELPERS
  const isOpenDeleteLabelTicketModal = useMemo(() => {
    return modalActions.checkIsOpenModal('DeleteLabelTicketModal');
  }, [modalActions]);

  const isDisabledForm = useMemo(() => {
    return mutationDeleteLabelTicket.isPending;
  }, [mutationDeleteLabelTicket.isPending]);

  const isLoadingForm = useMemo(() => {
    return mutationDeleteLabelTicket.isPending;
  }, [mutationDeleteLabelTicket.isPending]);

  // EFFECTS
  useEffect(() => {
    if (props) {
      form.reset({
        labelTicketId: props?.labelTicketId || '',
      });
    }
  }, [props?.labelTicketId, form]);

  // HANDLERS
  const handleSubmitForm = (data: TDeleteLabelTicketValidation) => {
    mutationDeleteLabelTicket.mutate({
      labelTicketId: data?.labelTicketId || '',
    });
  };

  const handleCloseModal = () => {
    modalActions.closeModal('DeleteLabelTicketModal');
  };

  const handleClose = useCallback(() => {
    handleCloseModal();
    form.reset();
  }, [handleCloseModal, form]);

  return (
    <Modal
      isOpen={isOpenDeleteLabelTicketModal}
      lockBodyScroll
      onClose={handleClose}
      className="p-[32px] md:w-[500px] w-[345px]"
    >
      <ModalHeader>
        <ModalTitle>Delete Label Ticketing</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(handleSubmitForm)(e);
          }}
          id="delete-label-ticketing-form"
          className="grid gap-[16px]"
        >
          <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to delete this label ticketing?</span>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="delete-label-ticketing-form" type="submit" disabled={isDisabledForm}>
          {isLoadingForm ? <Spinner className="w-[18px] h-[18px]" /> : null} Delete
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});
DeleteLabelTicketModal.displayName = 'DeleteLabelTicketModal';

export default DeleteLabelTicketModal;
